import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { getDownloadURL, ref } from "firebase/storage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { storage } from "../../services/firebase"; // Asegúrate de que esto esté configurado

const SubscriptionModal = React.memo(({ open, onClose }) => {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState(false);

  // Mover getText aquí arriba y envolverlo en useCallback
  const getText = useCallback(
    (key, defaultText) => t(key, { defaultValue: defaultText }),
    [t],
  );

  // Evitar re-renderizados usando useCallback
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Ahora useMemo tiene la dependencia estable getText
  const translatedTexts = useMemo(
    () => ({
      title: getText(
        "chatbot.subscriptionModal.title",
        "Accede a tu Asistente Personalizado Vic",
      ),
      message: getText(
        "chatbot.subscriptionModal.message",
        "El asistente virtual Vic...",
      ),
      // otros textos
    }),
    [getText], // Ahora getText es estable entre renderizados
  );

  // Cargar imagen con estrategia de respaldo múltiple
  useEffect(() => {
    let isMounted = true;

    const fetchLogo = async () => {
      try {
        // ESTRATEGIA 1: Intenta primero desde carpeta pública
        const publicPaths = [
          "/assets/images/chatbot/vic-logo.png", // Primera opción
          "/assets/vic-logo.png", // Segunda opción
          "/assets/images/chatbot/vic-logo-fallback.png", // Tercera opción (ya está definida como respaldo)
        ];

        // Probar cada ruta en orden hasta encontrar una que funcione
        for (const path of publicPaths) {
          const exists = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = path;
          });

          if (exists && isMounted) {
            setLogoUrl(path);
            setLogoError(false); // Asegurar que no hay error
            return; // Salir si encontramos una imagen válida
          }
        }

        // ESTRATEGIA 2: Si ninguna ruta pública funciona, intentar Firebase Storage
        const logoRef = ref(storage, "images/chatbot/vic-logo.png");
        const url = await getDownloadURL(logoRef);
        if (isMounted) {
          setLogoUrl(url);
          setLogoError(false);
        }
      } catch (error) {
        if (isMounted) {
          setLogoError(true);
        }
      }
    };

    // Solo cargar el logo cuando el modal está abierto (mantener comportamiento original)
    if (open) fetchLogo();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 },
      }}
      aria-labelledby="subscription-modal-title"
      aria-describedby="subscription-modal-description"
      // Asegurar que se puede cerrar con Escape
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleClose();
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }} id="subscription-modal-title">
        <Typography variant="h5" component="div" fontWeight="bold">
          {translatedTexts.title}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 }, // Menos padding en móviles
        }}
      >
        <Box
          sx={{
            display: "flex",
            mb: 3,
            alignItems: "center",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" }, // Vertical en móviles
          }}
        >
          <Box
            component="img"
            src={
              logoError
                ? "/assets/images/chatbot/vic-logo-fallback.png"
                : logoUrl
            }
            alt={getText("chatbot.logoAlt", "Vic - Asistente Virtual")}
            onError={(e) => {
              // Si falla incluso el fallback, intentar una última opción
              if (e.target.src !== "/assets/images/default-avatar.png") {
                e.target.src = "/assets/images/default-avatar.png";
              } else {
                // Si todo falla, ocultar la imagen
                e.target.style.display = "none";
              }
              setLogoError(true);
            }}
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: (theme) => `3px solid ${theme.palette.primary.main}`,
              objectFit: "cover",
            }}
            loading="lazy"
          />
          {/* --- TEXTO PRINCIPAL MODIFICADO --- */}
          <Typography variant="body1" id="subscription-modal-description">
            {translatedTexts.message}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            bgcolor: "primary.light", // Usar un color claro del tema
            borderRadius: 2,
            color: "primary.contrastText", // Asegurar contraste
          }}
          role="region"
          aria-label={getText(
            "chatbot.subscriptionModal.benefitsAriaLabel", // Clave aria-label diferente
            "Beneficios de unirte a nuestra comunidad",
          )}
        >
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {getText(
              "chatbot.subscriptionModal.benefitsTitle", // Clave de título diferente
              "Al unirte, disfrutarás de:",
            )}
          </Typography>
          {/* --- LISTA DE BENEFICIOS MODIFICADA --- */}
          <ul style={{ paddingLeft: "20px", marginTop: "8px" }}>
            <li>
              {getText(
                "chatbot.subscriptionModal.benefit1",
                "Acceso ilimitado al asistente Vic para resolver tus dudas", // Más directo
              )}
            </li>
            <li>
              {getText(
                "chatbot.subscriptionModal.benefit2",
                "Información actualizada y fiable basada en conocimiento experto y experiencias compartidas", // Enfatiza comunidad
              )}
            </li>
            <li>
              {getText(
                "chatbot.subscriptionModal.benefit3",
                "Apoyo constante para acompañarte en cada etapa", // Más cálido que "24/7"
              )}
            </li>
            <li>
              {getText(
                "chatbot.subscriptionModal.benefit4",
                "Recursos valiosos y exclusivos para familias como la tuya", // Más personal
              )}
            </li>
          </ul>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" }, // Vertical en móviles pequeños
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" }, // Ancho completo en móviles
            mb: { xs: 1, sm: 0 }, // Margen en móviles
          },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          aria-label={getText(
            "chatbot.subscriptionModal.cancelAriaLabel", // Clave aria-label diferente
            "Cerrar esta ventana",
          )}
          sx={{
            minHeight: "44px", // Área táctil adecuada
          }}
        >
          {getText("chatbot.subscriptionModal.cancel", "Ahora no")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/subscriptions" // Enlace correcto
          sx={{ px: 3 }}
          aria-label={getText(
            "chatbot.subscriptionModal.subscribeAriaLabel", // Clave aria-label diferente
            "Descubrir planes de suscripción",
          )}
        >
          {getText(
            "chatbot.subscriptionModal.subscribe",
            "Descubrir planes", // Más corto y atractivo
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SubscriptionModal;
